'use client';

import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useFrameRate } from '@/hooks/useFrameRate';

interface FpsIndicatorProps {
  width?: number;
  height?: number;
}

export const SlidingNeedle: FC<FpsIndicatorProps> = ({
  width = 200,
  height = 8,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { normalized } = useFrameRate();
  const smoothedRef = useRef(normalized);
  const trailRef = useRef<number[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    smoothedRef.current += (normalized - smoothedRef.current) * 0.15;
    const needleX = smoothedRef.current * (width - 8) + 4;

    trailRef.current.push(needleX);
    if (trailRef.current.length > 30) trailRef.current.shift();

    ctx.clearRect(0, 0, width, height);

    // Small ticks (3 between each prominent tick = 40 total)
    ctx.fillStyle = '#333';
    for (let i = 0; i <= 40; i++) {
      const x = 4 + (i / 40) * (width - 8);
      ctx.fillRect(x, height / 2 - 1, 1, 2);
    }

    // Prominent ticks (every 10%)
    ctx.fillStyle = '#555';
    for (let i = 0; i <= 10; i++) {
      const x = 4 + (i / 10) * (width - 8);
      ctx.fillRect(x, height / 2 - 3, 1, 6);
    }

    ctx.fillStyle = '#888';
    ctx.fillRect(4, height / 2 - 4, 1, 8);
    ctx.fillRect(4 + (width - 8), height / 2 - 4, 1, 8);

    trailRef.current.forEach((x, i) => {
      const alpha = (i / trailRef.current.length) * 0.5;
      ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      ctx.fillRect(x - 0.5, 2, 1, height - 4);
    });

    ctx.fillStyle = '#fff';
    ctx.fillRect(needleX - 1, 0, 2, height);
  }, [normalized, width, height]);

  return <canvas ref={canvasRef} style={{ width, height, display: 'block' }} />;
};
